import Logo from "@/components/Logo";
import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const STAFF = ["super_admin", "admin", "content_editor", "coach"];

export default function Login() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate(STAFF.includes(user.role) ? "/admin" : "/learn", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = mode === "login" ? await login(email, password) : await register(name, email, password);
      navigate(STAFF.includes(u.role) ? "/admin" : "/learn", { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#FAFAFA] flex flex-col">
      <div className="grain" />
      <div className="px-6 lg:px-8 h-20 flex items-center max-w-7xl mx-auto w-full">
        <Link to="/" data-testid="login-logo" aria-label="MBM home">
          <Logo className="h-7 w-auto" />
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 pb-20">
        <div className="w-full max-w-md">
          <div className="section-label text-[10px] text-[#9fd3ff]">MBM Trading Trainer</div>
          <h1 className="mt-4 font-heading text-3xl sm:text-4xl font-light tracking-tight">
            {mode === "login" ? "Welcome back." : "Start learning."}
          </h1>
          <p className="mt-3 text-sm text-zinc-500 font-light">
            {mode === "login"
              ? "Sign in to continue your journey."
              : "Create your free account to begin the curriculum."}
          </p>

          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <label className="section-label text-[9px] text-zinc-500">Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="mt-2 w-full bg-transparent border border-white/15 focus:border-[#6ea8ff]/60 rounded-lg px-4 py-3 text-sm outline-none transition-colors"
                  data-testid="auth-name"
                />
              </div>
            )}
            <div>
              <label className="section-label text-[9px] text-zinc-500">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-2 w-full bg-transparent border border-white/15 focus:border-[#6ea8ff]/60 rounded-lg px-4 py-3 text-sm outline-none transition-colors"
                data-testid="auth-email"
              />
            </div>
            <div>
              <label className="section-label text-[9px] text-zinc-500">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-2 w-full bg-transparent border border-white/15 focus:border-[#6ea8ff]/60 rounded-lg px-4 py-3 text-sm outline-none transition-colors"
                data-testid="auth-password"
              />
              {mode === "login" && (
                <div className="mt-2 text-right">
                  <Link to="/forgot-password" className="text-[11px] text-zinc-500 hover:text-[#9fd3ff] transition-colors" data-testid="auth-forgot-link">
                    Forgot password?
                  </Link>
                </div>
              )}
            </div>

            {error && (
              <div className="text-sm text-red-400 font-light" data-testid="auth-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="btn-solid w-full py-3.5 text-sm font-medium tracking-wide disabled:opacity-50"
              data-testid="auth-submit"
            >
              {busy ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>

          <div className="mt-8 text-sm text-zinc-500 font-light">
            {mode === "login" ? "New to MBM?" : "Already have an account?"}{" "}
            <button
              type="button"
              onClick={() => {
                setMode(mode === "login" ? "register" : "login");
                setError("");
              }}
              className="text-[#9fd3ff] hover:text-white transition-colors"
              data-testid="auth-toggle"
            >
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </div>

          <div className="mt-6 text-[11px] text-zinc-600 font-light">
            Social sign in (Google, Apple) coming soon.
          </div>
        </div>
      </div>
    </div>
  );
}
